import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import {
  Share2,
  Plus,
  Copy,
  Trash2,
  Pencil,
  MessageCircle,
  Package,
  Users,
  TrendingUp,
  Wallet,
  CheckCircle2,
  MousePointerClick,
  Power,
  Download,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { affiliateApi, formatPrecio, waLink, formatPeriodo, buildCommissionPaidMessage, buildBulkPaidMessage } from "@/lib/agenda";

const WHATSAPP_PHONE = "+34617921200";
const WHATSAPP_MSG = "Hola, quiero información sobre el programa de afiliados";

const emptyProduct = { name: "", price: "", pay_type: "unico", commission_type: "percent", commission_value: "" };
const emptyAffiliate = { name: "", email: "", phone: "" };
const emptySale = { affiliate_id: "", product_id: "", client_name: "", start_date: new Date().toISOString().slice(0, 10) };

export default function Afiliados() {
  const [products, setProducts] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [sales, setSales] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [referrals, setReferrals] = useState([]);

  const [prodOpen, setProdOpen] = useState(false);
  const [prodForm, setProdForm] = useState(emptyProduct);
  const [prodEditId, setProdEditId] = useState(null);

  const [affOpen, setAffOpen] = useState(false);
  const [affForm, setAffForm] = useState(emptyAffiliate);
  const [affEditId, setAffEditId] = useState(null);

  const [saleOpen, setSaleOpen] = useState(false);
  const [saleForm, setSaleForm] = useState(emptySale);

  const load = useCallback(async () => {
    const [p, a, s, c, r] = await Promise.all([
      affiliateApi.listProducts(),
      affiliateApi.listAffiliates(),
      affiliateApi.listSales(),
      affiliateApi.listCommissions(),
      affiliateApi.listReferrals(),
    ]);
    setProducts(p);
    setAffiliates(a);
    setSales(s);
    setCommissions(c);
    setReferrals(r);
  }, []);

  useEffect(() => { load(); }, [load]);

  const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const affiliateById = useMemo(() => Object.fromEntries(affiliates.map((a) => [a.id, a])), [affiliates]);

  const statsByAffiliate = useMemo(() => {
    const m = {};
    affiliates.forEach((a) => (m[a.id] = { pending: 0, paid: 0, sales: 0, clicks: 0 }));
    commissions.forEach((c) => {
      if (!m[c.affiliate_id]) return;
      if (c.status === "pagada") m[c.affiliate_id].paid += c.amount || 0;
      else m[c.affiliate_id].pending += c.amount || 0;
    });
    sales.forEach((s) => { if (m[s.affiliate_id]) m[s.affiliate_id].sales += 1; });
    referrals.forEach((r) => { if (m[r.affiliate_id]) m[r.affiliate_id].clicks += 1; });
    return m;
  }, [affiliates, commissions, sales, referrals]);

  const totals = useMemo(() => {
    const pending = commissions.filter((c) => c.status !== "pagada").reduce((s, c) => s + (c.amount || 0), 0);
    const paid = commissions.filter((c) => c.status === "pagada").reduce((s, c) => s + (c.amount || 0), 0);
    return {
      pending,
      paid,
      sales: sales.length,
      affiliates: affiliates.filter((a) => a.active !== false).length,
    };
  }, [commissions, sales, affiliates]);

  const affiliateLink = (code) => `${window.location.origin}/?ref=${code}`;
  const portalLink = (code) => `${window.location.origin}/afiliado/${code}`;

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Enlace copiado.");
    } catch {
      toast.error("No se pudo copiar. Cópialo a mano.");
    }
  };
  const copyLink = (code) => copyText(affiliateLink(code));
  const copyPortal = (code) => copyText(portalLink(code));

  // ---- Productos ----
  const openNewProduct = () => { setProdEditId(null); setProdForm(emptyProduct); setProdOpen(true); };
  const openEditProduct = (p) => {
    setProdEditId(p.id);
    setProdForm({ name: p.name, price: String(p.price ?? ""), pay_type: p.pay_type, commission_type: p.commission_type, commission_value: String(p.commission_value ?? "") });
    setProdOpen(true);
  };
  const saveProduct = async () => {
    if (!prodForm.name.trim()) { toast.error("Pon un nombre al producto."); return; }
    const payload = {
      name: prodForm.name.trim(),
      price: Number(prodForm.price) || 0,
      pay_type: prodForm.pay_type,
      commission_type: prodForm.commission_type,
      commission_value: Number(prodForm.commission_value) || 0,
    };
    try {
      if (prodEditId) { await affiliateApi.updateProduct(prodEditId, payload); toast.success("Producto actualizado."); }
      else { await affiliateApi.createProduct(payload); toast.success("Producto creado."); }
      setProdOpen(false);
      await load();
    } catch { toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde."); }
  };
  const deleteProduct = async (id) => { await affiliateApi.deleteProduct(id); toast.success("Producto borrado."); await load(); };

  // ---- Afiliados ----
  const openNewAffiliate = () => { setAffEditId(null); setAffForm(emptyAffiliate); setAffOpen(true); };
  const openEditAffiliate = (a) => { setAffEditId(a.id); setAffForm({ name: a.name, email: a.email || "", phone: a.phone || "" }); setAffOpen(true); };
  const saveAffiliate = async () => {
    if (!affForm.name.trim()) { toast.error("Pon el nombre del afiliado."); return; }
    const payload = { name: affForm.name.trim(), email: affForm.email.trim(), phone: affForm.phone.trim() };
    try {
      if (affEditId) { await affiliateApi.updateAffiliate(affEditId, payload); toast.success("Afiliado actualizado."); }
      else { await affiliateApi.createAffiliate(payload); toast.success("Afiliado dado de alta."); }
      setAffOpen(false);
      await load();
    } catch { toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde."); }
  };
  const deleteAffiliate = async (id) => { await affiliateApi.deleteAffiliate(id); toast.success("Afiliado borrado."); await load(); };

  // ---- Ventas ----
  const openNewSale = () => {
    if (affiliates.length === 0 || products.length === 0) {
      toast.error("Crea antes un afiliado y un producto.");
      return;
    }
    setSaleForm({ ...emptySale, affiliate_id: affiliates[0].id, product_id: products[0].id });
    setSaleOpen(true);
  };
  const saveSale = async () => {
    if (!saleForm.affiliate_id || !saleForm.product_id || !saleForm.start_date) { toast.error("Elige afiliado, producto y fecha."); return; }
    try {
      await affiliateApi.createSale({ ...saleForm, client_name: saleForm.client_name.trim() });
      toast.success("Venta registrada.");
      setSaleOpen(false);
      await load();
    } catch { toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde."); }
  };
  const toggleSale = async (s) => { await affiliateApi.updateSale(s.id, { active: !s.active }); toast.success(s.active ? "Suscripción dada de baja." : "Suscripción reactivada."); await load(); };
  const deleteSale = async (id) => { await affiliateApi.deleteSale(id); toast.success("Venta borrada."); await load(); };

  // ---- Comisiones ----
  const payAll = async (a) => {
    const st = statsByAffiliate[a.id] || {};
    if (!st.pending) { toast.message("No hay comisiones pendientes."); return; }
    try {
      const res = await affiliateApi.payAllCommissions(a.id);
      if (a.phone && res.updated > 0) {
        window.open(waLink(a.phone, buildBulkPaidMessage(a, res.total, res.updated)), "_blank");
      }
      toast.success(`${res.updated} ${res.updated === 1 ? "comisión marcada" : "comisiones marcadas"} como pagadas.`);
      await load();
    } catch { toast.error("Algo no ha ido bien. Prueba otra vez o vuelve más tarde."); }
  };

  const toggleCommission = async (c) => {
    const next = c.status === "pagada" ? "pendiente" : "pagada";
    await affiliateApi.updateCommission(c.id, { status: next });
    if (next === "pagada") {
      const aff = affiliateById[c.affiliate_id];
      const prod = productById[c.product_id];
      if (aff && aff.phone) {
        window.open(waLink(aff.phone, buildCommissionPaidMessage(aff, c, prod)), "_blank");
      } else {
        toast.message("Comisión marcada como pagada. El afiliado no tiene teléfono para avisar.");
      }
    }
    await load();
  };

  const exportCSV = () => {
    if (commissions.length === 0) {
      toast.error("No hay comisiones para exportar.");
      return;
    }
    const header = ["Periodo", "Afiliado", "Email", "Telefono", "Producto", "Importe", "Estado"];
    const rows = [...commissions]
      .sort((a, b) => (a.period < b.period ? 1 : -1))
      .map((c) => {
        const aff = affiliateById[c.affiliate_id] || {};
        const prod = productById[c.product_id] || {};
        return [
          formatPeriodo(c.period),
          aff.name || "",
          aff.email || "",
          aff.phone || "",
          prod.name || "",
          String(c.amount ?? 0).replace(".", ","),
          c.status === "pagada" ? "Pagada" : "Pendiente",
        ];
      });
    const csv = [header, ...rows]
      .map((r) => r.map((f) => `"${String(f).replace(/"/g, '""')}"`).join(";"))
      .join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `comisiones_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Resumen descargado.");
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="afiliados-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="flex items-center justify-center h-11 w-11 rounded-2xl text-white" style={{ backgroundColor: "#2E2A8F" }}>
            <Share2 className="h-5 w-5" />
          </span>
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Afiliados</h1>
            <p className="text-sm text-slate-500 mt-0.5">Da de alta afiliados, genera su enlace y controla sus comisiones.</p>
          </div>
        </div>
        <a
          href={waLink(WHATSAPP_PHONE, WHATSAPP_MSG)}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-white text-sm font-medium shrink-0"
          style={{ backgroundColor: "#0E9F6E" }}
          data-testid="afiliados-whatsapp"
        >
          <MessageCircle className="h-4 w-4" /> Escríbeme por WhatsApp
        </a>
      </div>

      <Tabs defaultValue="resumen" className="w-full">
        <TabsList className="rounded-full bg-slate-100 p-1 flex-wrap h-auto" data-testid="afiliados-tabs">
          <TabsTrigger value="resumen" className="rounded-full data-[state=active]:bg-white" data-testid="tab-resumen">Resumen</TabsTrigger>
          <TabsTrigger value="afiliados" className="rounded-full data-[state=active]:bg-white" data-testid="tab-afiliados">Afiliados</TabsTrigger>
          <TabsTrigger value="productos" className="rounded-full data-[state=active]:bg-white" data-testid="tab-productos">Productos</TabsTrigger>
          <TabsTrigger value="ventas" className="rounded-full data-[state=active]:bg-white" data-testid="tab-ventas">Ventas</TabsTrigger>
          <TabsTrigger value="comisiones" className="rounded-full data-[state=active]:bg-white" data-testid="tab-comisiones">Comisiones</TabsTrigger>
        </TabsList>

        {/* Resumen */}
        <TabsContent value="resumen" className="mt-5 space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={Users} label="Afiliados activos" value={totals.affiliates} />
            <Stat icon={TrendingUp} label="Ventas registradas" value={totals.sales} />
            <Stat icon={Wallet} label="Comisión pendiente" value={formatPrecio(totals.pending)} tone="pending" />
            <Stat icon={CheckCircle2} label="Comisión pagada" value={formatPrecio(totals.paid)} tone="paid" />
          </div>
          {affiliates.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no tienes afiliados. Da de alta el primero en la pestaña Afiliados.</p>
          ) : (
            <div className="bg-white rounded-2xl border border-[#EAE5DF] overflow-hidden">
              {affiliates.map((a) => {
                const st = statsByAffiliate[a.id] || {};
                return (
                  <div key={a.id} className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0 text-sm">
                    <span className="font-medium text-slate-900">{a.name}</span>
                    <div className="flex items-center gap-5 text-xs text-slate-500">
                      <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5" /> {st.clicks || 0} clics</span>
                      <span>{st.sales || 0} ventas</span>
                      <span className="text-amber-700 font-medium">{formatPrecio(st.pending || 0)} pend.</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Afiliados */}
        <TabsContent value="afiliados" className="mt-5 space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewAffiliate} className="rounded-full text-white gap-2" style={{ backgroundColor: "#275FBE" }} data-testid="add-affiliate-button">
              <Plus className="h-4 w-4" /> Dar de alta afiliado
            </Button>
          </div>
          {affiliates.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no tienes afiliados. Añade el primero cuando quieras.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="affiliates-list">
              {affiliates.map((a) => {
                const st = statsByAffiliate[a.id] || {};
                return (
                  <div key={a.id} className="bg-white rounded-2xl border border-[#EAE5DF] p-5 shadow-[0_6px_24px_-12px_rgba(46,42,143,0.10)]" data-testid={`affiliate-card-${a.id}`}>
                    <div className="flex items-start justify-between">
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 truncate">{a.name}</p>
                        <p className="text-xs text-slate-500">{a.email || "Sin email"} · {a.phone || "Sin teléfono"}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => openEditAffiliate(a)} className="text-slate-400 hover:text-[#275FBE] p-1" title="Editar" data-testid={`edit-affiliate-${a.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => deleteAffiliate(a.id)} className="text-slate-400 hover:text-red-500 p-1" title="Borrar" data-testid={`delete-affiliate-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      <div>
                        <p className="text-[11px] text-slate-400 mb-1">Enlace de venta (para compartir)</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 truncate text-slate-600" data-testid={`affiliate-link-${a.id}`}>{affiliateLink(a.code)}</code>
                          <button onClick={() => copyLink(a.code)} className="inline-flex items-center gap-1 text-xs font-medium text-[#275FBE] border border-[#275FBE] rounded-lg px-2.5 py-1.5 hover:bg-[#275FBE] hover:text-white transition-colors shrink-0" data-testid={`copy-link-${a.id}`}>
                            <Copy className="h-3.5 w-3.5" /> Copiar
                          </button>
                        </div>
                      </div>
                      <div>
                        <p className="text-[11px] text-slate-400 mb-1">Portal del afiliado (para que vea sus comisiones)</p>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5 truncate text-slate-600" data-testid={`portal-link-${a.id}`}>{portalLink(a.code)}</code>
                          <a href={portalLink(a.code)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1.5 hover:bg-slate-100 transition-colors shrink-0" data-testid={`open-portal-${a.id}`}>
                            <ExternalLink className="h-3.5 w-3.5" /> Abrir
                          </a>
                          <button onClick={() => copyPortal(a.code)} className="inline-flex items-center gap-1 text-xs font-medium text-[#275FBE] border border-[#275FBE] rounded-lg px-2.5 py-1.5 hover:bg-[#275FBE] hover:text-white transition-colors shrink-0" data-testid={`copy-portal-${a.id}`}>
                            <Copy className="h-3.5 w-3.5" /> Copiar
                          </button>
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                      <span>{st.clicks || 0} clics · {st.sales || 0} ventas</span>
                      <span className="text-amber-700 font-medium">{formatPrecio(st.pending || 0)} pendiente</span>
                    </div>
                    {st.pending > 0 && (
                      <button
                        onClick={() => payAll(a)}
                        className="mt-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-medium rounded-full py-2 border border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                        data-testid={`pay-all-${a.id}`}
                      >
                        <CheckCircle2 className="h-3.5 w-3.5" /> Marcar todo pagado ({formatPrecio(st.pending)})
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Productos */}
        <TabsContent value="productos" className="mt-5 space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewProduct} className="rounded-full text-white gap-2" style={{ backgroundColor: "#275FBE" }} data-testid="add-product-button">
              <Plus className="h-4 w-4" /> Crear producto
            </Button>
          </div>
          {products.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no tienes productos. Crea el primero para asignar comisiones.</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="products-list">
              {products.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-[#EAE5DF] p-5 shadow-[0_6px_24px_-12px_rgba(46,42,143,0.10)]" data-testid={`product-card-${p.id}`}>
                  <div className="flex items-start justify-between">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-[#2E2A8F]" />
                        <p className="font-medium text-slate-900 truncate">{p.name}</p>
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatPrecio(p.price)} · {p.pay_type === "mensual" ? "Pago mensual" : "Pago único"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => openEditProduct(p)} className="text-slate-400 hover:text-[#275FBE] p-1" data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => deleteProduct(p.id)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600">
                    Comisión: <strong>{p.commission_type === "fixed" ? formatPrecio(p.commission_value) : `${p.commission_value}%`}</strong>
                    {p.pay_type === "mensual" ? " cada mes" : " una vez"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Ventas */}
        <TabsContent value="ventas" className="mt-5 space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewSale} className="rounded-full text-white gap-2" style={{ backgroundColor: "#275FBE" }} data-testid="add-sale-button">
              <Plus className="h-4 w-4" /> Registrar venta
            </Button>
          </div>
          {sales.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no hay ventas. Registra la primera cuando entre una compra por un afiliado.</p>
          ) : (
            <div className="space-y-2" data-testid="sales-list">
              {sales.map((s) => {
                const aff = affiliateById[s.affiliate_id];
                return (
                  <div key={s.id} className="bg-white rounded-2xl border border-[#EAE5DF] p-4 flex items-center justify-between gap-3" data-testid={`sale-card-${s.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{s.product_name} · {formatPrecio(s.price)}</p>
                      <p className="text-xs text-slate-500">
                        {aff ? aff.name : "—"}{s.client_name ? ` → ${s.client_name}` : ""} · {s.start_date}
                        {" · "}
                        <span className={s.pay_type === "mensual" ? "text-[#2E2A8F]" : ""}>
                          {s.pay_type === "mensual" ? "mensual" : "único"}
                        </span>
                        {s.pay_type === "mensual" && !s.active && <span className="text-red-500"> · dado de baja</span>}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">Comisión: {formatPrecio(s.commission_each)}{s.pay_type === "mensual" ? "/mes" : ""}</p>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {s.pay_type === "mensual" && (
                        <button onClick={() => toggleSale(s)} className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-full border ${s.active ? "border-slate-200 text-slate-600" : "border-emerald-200 text-emerald-700 bg-emerald-50"}`} title={s.active ? "Dar de baja" : "Reactivar"} data-testid={`toggle-sale-${s.id}`}>
                          <Power className="h-3.5 w-3.5" /> {s.active ? "Baja" : "Alta"}
                        </button>
                      )}
                      <button onClick={() => deleteSale(s.id)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`delete-sale-${s.id}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Comisiones */}
        <TabsContent value="comisiones" className="mt-5 space-y-4">
          <div className="flex justify-end">
            <Button onClick={exportCSV} variant="outline" className="rounded-full gap-2 border-[#275FBE] text-[#275FBE] hover:bg-[#275FBE] hover:text-white" data-testid="export-commissions-button">
              <Download className="h-4 w-4" /> Exportar resumen (CSV)
            </Button>
          </div>
          {commissions.length === 0 ? (
            <p className="text-sm text-slate-400">Todavía no hay comisiones. Se generan al registrar ventas.</p>
          ) : (
            <div className="bg-white rounded-2xl border border-[#EAE5DF] overflow-hidden" data-testid="commissions-list">
              {commissions.map((c) => {
                const aff = affiliateById[c.affiliate_id];
                const prod = productById[c.product_id];
                return (
                  <div key={c.id} className="flex items-center justify-between px-5 py-3 border-b border-slate-100 last:border-0 gap-3" data-testid={`commission-row-${c.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{aff ? aff.name : "—"} · {formatPrecio(c.amount)}</p>
                      <p className="text-xs text-slate-500">{prod ? prod.name : ""} · {formatPeriodo(c.period)}</p>
                    </div>
                    <button
                      onClick={() => toggleCommission(c)}
                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border shrink-0 ${c.status === "pagada" ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}
                      data-testid={`toggle-commission-${c.id}`}
                    >
                      {c.status === "pagada" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Wallet className="h-3.5 w-3.5" />}
                      {c.status === "pagada" ? "Pagada" : "Pendiente"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Modal producto */}
      <Dialog open={prodOpen} onOpenChange={setProdOpen}>
        <DialogContent className="sm:max-w-[440px] rounded-3xl" data-testid="product-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900">{prodEditId ? "Editar producto" : "Crear producto"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Define el precio y la comisión del afiliado.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="p-name">Nombre</Label>
              <Input id="p-name" value={prodForm.name} onChange={(e) => setProdForm({ ...prodForm, name: e.target.value })} data-testid="product-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="p-price">Precio (€)</Label>
                <Input id="p-price" type="number" value={prodForm.price} onChange={(e) => setProdForm({ ...prodForm, price: e.target.value })} data-testid="product-price" />
              </div>
              <div className="space-y-1.5">
                <Label>Tipo de pago</Label>
                <Select value={prodForm.pay_type} onValueChange={(v) => setProdForm({ ...prodForm, pay_type: v })}>
                  <SelectTrigger data-testid="product-paytype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unico">Pago único</SelectItem>
                    <SelectItem value="mensual">Pago mensual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Comisión</Label>
                <Select value={prodForm.commission_type} onValueChange={(v) => setProdForm({ ...prodForm, commission_type: v })}>
                  <SelectTrigger data-testid="product-commtype"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Porcentaje (%)</SelectItem>
                    <SelectItem value="fixed">Importe fijo (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="p-commval">{prodForm.commission_type === "fixed" ? "Importe (€)" : "Porcentaje"}</Label>
                <Input id="p-commval" type="number" value={prodForm.commission_value} onChange={(e) => setProdForm({ ...prodForm, commission_value: e.target.value })} data-testid="product-commvalue" />
              </div>
            </div>
          </div>
          <Button className="w-full rounded-full text-white" style={{ backgroundColor: "#275FBE" }} onClick={saveProduct} data-testid="save-product-button">Guardar producto</Button>
        </DialogContent>
      </Dialog>

      {/* Modal afiliado */}
      <Dialog open={affOpen} onOpenChange={setAffOpen}>
        <DialogContent className="sm:max-w-[440px] rounded-3xl" data-testid="affiliate-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900">{affEditId ? "Editar afiliado" : "Dar de alta afiliado"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Al guardarlo se genera su enlace único.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="a-name">Nombre</Label>
              <Input id="a-name" value={affForm.name} onChange={(e) => setAffForm({ ...affForm, name: e.target.value })} data-testid="affiliate-name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="a-email">Email</Label>
              <Input id="a-email" type="email" value={affForm.email} onChange={(e) => setAffForm({ ...affForm, email: e.target.value })} data-testid="affiliate-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="a-phone">Teléfono</Label>
              <Input id="a-phone" value={affForm.phone} onChange={(e) => setAffForm({ ...affForm, phone: e.target.value })} data-testid="affiliate-phone" />
            </div>
          </div>
          <Button className="w-full rounded-full text-white" style={{ backgroundColor: "#275FBE" }} onClick={saveAffiliate} data-testid="save-affiliate-button">Guardar afiliado</Button>
        </DialogContent>
      </Dialog>

      {/* Modal venta */}
      <Dialog open={saleOpen} onOpenChange={setSaleOpen}>
        <DialogContent className="sm:max-w-[440px] rounded-3xl" data-testid="sale-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-slate-900">Registrar venta</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Asigna la compra a un afiliado para calcular su comisión.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Afiliado</Label>
              <Select value={saleForm.affiliate_id} onValueChange={(v) => setSaleForm({ ...saleForm, affiliate_id: v })}>
                <SelectTrigger data-testid="sale-affiliate"><SelectValue placeholder="Elige afiliado" /></SelectTrigger>
                <SelectContent>
                  {affiliates.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Producto</Label>
              <Select value={saleForm.product_id} onValueChange={(v) => setSaleForm({ ...saleForm, product_id: v })}>
                <SelectTrigger data-testid="sale-product"><SelectValue placeholder="Elige producto" /></SelectTrigger>
                <SelectContent>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.pay_type === "mensual" ? "mensual" : "único"})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="s-client">Cliente</Label>
                <Input id="s-client" value={saleForm.client_name} onChange={(e) => setSaleForm({ ...saleForm, client_name: e.target.value })} data-testid="sale-client" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="s-date">Fecha</Label>
                <Input id="s-date" type="date" value={saleForm.start_date} onChange={(e) => setSaleForm({ ...saleForm, start_date: e.target.value })} data-testid="sale-date" />
              </div>
            </div>
          </div>
          <Button className="w-full rounded-full text-white" style={{ backgroundColor: "#275FBE" }} onClick={saveSale} data-testid="save-sale-button">Guardar venta</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Stat = ({ icon: Icon, label, value, tone }) => {
  const bg = tone === "paid" ? "#ECFDF5" : tone === "pending" ? "#FFFBEB" : "#EAF1FB";
  const color = tone === "paid" ? "#047857" : tone === "pending" ? "#B45309" : "#275FBE";
  return (
    <div className="bg-white rounded-2xl border border-[#EAE5DF] p-4 shadow-[0_6px_24px_-14px_rgba(46,42,143,0.10)]">
      <span className="flex items-center justify-center h-9 w-9 rounded-xl" style={{ backgroundColor: bg, color }}>
        <Icon className="h-4.5 w-4.5" />
      </span>
      <p className="mt-3 text-xs text-slate-500">{label}</p>
      <p className="font-heading text-xl font-bold text-slate-900 mt-0.5">{value}</p>
    </div>
  );
};
